/**
 * =============================================================================
 * Hands Management Page
 * =============================================================================
 *
 * CRUD interface for managing hand hardware.
 *
 * Features:
 * - List all hands with tensometers
 * - Create new hand (auto-creates 5 tensometers)
 * - Edit hand details
 * - View calibration history
 *
 * Access: system_admin, institution_admin
 */

import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { handsApi, institutionsApi, Hand, HandCreate, HandUpdate, Calibration, Tensometer, TensometerUpdate } from '../services/api'
import { useAuth } from '../auth/AuthProvider'
import DataTable, { Column } from '../components/DataTable'
import Modal from '../components/Modal'

// =============================================================================
// Hand Form Component
// =============================================================================

interface HandFormProps {
  hand?: Hand | null
  onSubmit: (data: HandCreate | HandUpdate) => void
  onCancel: () => void
  loading?: boolean
}

/**
 * Form for creating/editing hands.
 */
const HandForm: React.FC<HandFormProps> = ({
  hand,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const { hasRole } = useAuth()
  const isEdit = !!hand

  // Fetch institutions for dropdown
  const { data: institutions } = useQuery({
    queryKey: ['institutions'],
    queryFn: () => institutionsApi.list(),
    enabled: hasRole('system_admin'),
  })

  const { data: myInstitution } = useQuery({
    queryKey: ['institutions-me'],
    queryFn: () => institutionsApi.me(),
    enabled: !hasRole('system_admin'),
  })

  const [formData, setFormData] = useState({
    hardware_id: hand?.hardware_id || '',
    hand_type: hand?.hand_type || 'right',
    hardware_version: hand?.hardware_version || '2.31',
    institution_id: hand?.institution_id || '',
    manufacturer: hand?.manufacturer || 'TUKE',
    status: hand?.status || 'active',
  })

  useEffect(() => {
    if (!hasRole('system_admin') && !isEdit && !formData.institution_id && myInstitution?.data?.id) {
      setFormData((prev) => ({ ...prev, institution_id: myInstitution.data.id }))
    }
  }, [hasRole, isEdit, formData.institution_id, myInstitution])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (isEdit) {
      onSubmit({
        institution_id: formData.institution_id || undefined,
        status: formData.status,
      } as HandUpdate)
    } else {
      onSubmit({
        hardware_id: formData.hardware_id,
        hand_type: formData.hand_type,
        hardware_version: formData.hardware_version,
        institution_id: formData.institution_id || undefined,
        manufacturer: formData.manufacturer || undefined,
      } as HandCreate)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Hardware ID - only for create */}
      {!isEdit && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Hardware ID *
          </label>
          <input
            type="text"
            className="input"
            value={formData.hardware_id}
            onChange={(e) => setFormData({ ...formData, hardware_id: e.target.value })}
            placeholder="HAND-R-001"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Unique identifier from the hardware label
          </p>
        </div>
      )}

      {/* Hand Type - only for create */}
      {!isEdit && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Hand Type *
          </label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="hand_type"
                value="left"
                checked={formData.hand_type === 'left'}
                onChange={(e) => setFormData({ ...formData, hand_type: e.target.value })}
                className="h-4 w-4 text-primary-600"
              />
              <span>Left Hand</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="hand_type"
                value="right"
                checked={formData.hand_type === 'right'}
                onChange={(e) => setFormData({ ...formData, hand_type: e.target.value })}
                className="h-4 w-4 text-primary-600"
              />
              <span>Right Hand</span>
            </label>
          </div>
        </div>
      )}

      {/* Hardware Version - only for create */}
      {!isEdit && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Hardware Version *
          </label>
          <input
            type="text"
            className="input"
            value={formData.hardware_version}
            onChange={(e) => setFormData({ ...formData, hardware_version: e.target.value })}
            placeholder="2.31"
            required
          />
        </div>
      )}

      {/* Manufacturer - only for create */}
      {!isEdit && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Manufacturer
          </label>
          <input
            type="text"
            className="input"
            value={formData.manufacturer}
            onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
            placeholder="TUKE"
          />
        </div>
      )}

      {/* Institution */}
      {hasRole('system_admin') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Assigned Institution
          </label>
          <select
            className="input"
            value={formData.institution_id}
            onChange={(e) => setFormData({ ...formData, institution_id: e.target.value })}
          >
            <option value="">Not assigned</option>
            {institutions?.data?.map((inst) => (
              <option key={inst.id} value={inst.id}>
                {inst.institution_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Status - edit only */}
      {isEdit && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            className="input"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          >
            <option value="active">Active</option>
            <option value="maintenance">Maintenance</option>
            <option value="retired">Retired</option>
          </select>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  )
}

// =============================================================================
// Tensometer Calibration Component
// =============================================================================

interface TensometerCalibrationProps {
  handId: string
  onClose: () => void
}

/**
 * Display and edit tensometer calibration values for a hand.
 */
const TensometerCalibration: React.FC<TensometerCalibrationProps> = ({ handId, onClose }) => {
  const queryClient = useQueryClient()

  // Fetch hand details with tensometers
  const { data: handData, isLoading } = useQuery({
    queryKey: ['hand', handId],
    queryFn: () => handsApi.get(handId),
  })

  const hand = handData?.data

  // Local state for editing
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValues, setEditValues] = useState<TensometerUpdate>({})

  // Update tensometer mutation
  const updateMutation = useMutation({
    mutationFn: ({ tensometerId, data }: { tensometerId: string; data: TensometerUpdate }) =>
      handsApi.updateTensometer(handId, tensometerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hands'] })
      queryClient.invalidateQueries({ queryKey: ['hand', handId] })
      setEditingId(null)
      setEditValues({})
    },
  })

  const handleEdit = (tensometer: Tensometer) => {
    setEditingId(tensometer.id)
    setEditValues({
      calibration_offset: tensometer.calibration_offset,
      calibration_scale: tensometer.calibration_scale,
    })
  }

  const handleSave = (tensometerId: string) => {
    updateMutation.mutate({ tensometerId, data: editValues })
  }

  const handleCancel = () => {
    setEditingId(null)
    setEditValues({})
  }

  const fingerLabels: Record<string, string> = {
    thumb: 'Thumb (Palec)',
    index: 'Index (Ukazovák)',
    middle: 'Middle (Prostredník)',
    ring: 'Ring (Prstenník)',
    little: 'Little (Malíček)',
  }

  if (isLoading) {
    return <div className="text-center py-8">Loading tensometers...</div>
  }

  if (!hand || !hand.tensometers || hand.tensometers.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No tensometers found for this hand.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600 mb-4">
        Calibration values for each tensometer. Offset and scale are used to normalize raw ADC values.
      </div>

      <div className="space-y-3">
        {hand.tensometers.map((tensometer) => (
          <div
            key={tensometer.id}
            className="border rounded-lg p-4 hover:border-primary-300 transition-colors"
          >
            <div className="flex justify-between items-start mb-3">
              <div>
                <span className="font-medium">
                  {fingerLabels[tensometer.finger_position] || tensometer.finger_position}
                </span>
                <p className="text-xs text-gray-500 font-mono">{tensometer.tensometer_id}</p>
              </div>
              <span className={`badge ${tensometer.status === 'active' ? 'badge-active' : 'badge-inactive'}`}>
                {tensometer.status}
              </span>
            </div>

            {editingId === tensometer.id ? (
              // Edit mode
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Offset</label>
                    <input
                      type="number"
                      step="0.01"
                      className="input"
                      value={editValues.calibration_offset ?? ''}
                      onChange={(e) =>
                        setEditValues({ ...editValues, calibration_offset: parseFloat(e.target.value) })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Scale</label>
                    <input
                      type="number"
                      step="0.01"
                      className="input"
                      value={editValues.calibration_scale ?? ''}
                      onChange={(e) =>
                        setEditValues({ ...editValues, calibration_scale: parseFloat(e.target.value) })
                      }
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={handleCancel}
                    className="btn-secondary text-sm py-1"
                    disabled={updateMutation.isPending}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSave(tensometer.id)}
                    className="btn-primary text-sm py-1"
                    disabled={updateMutation.isPending}
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              // View mode
              <div className="flex justify-between items-center">
                <div className="grid grid-cols-2 gap-8 text-sm">
                  <div>
                    <span className="text-gray-500">Offset:</span>{' '}
                    <span className="font-mono">{tensometer.calibration_offset}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Scale:</span>{' '}
                    <span className="font-mono">{tensometer.calibration_scale}</span>
                  </div>
                </div>
                <button
                  onClick={() => handleEdit(tensometer)}
                  className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                >
                  Edit
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-end pt-4 border-t">
        <button onClick={onClose} className="btn-secondary">
          Close
        </button>
      </div>
    </div>
  )
}

// =============================================================================
// Calibration History Component
// =============================================================================

interface CalibrationHistoryProps {
  handId: string
  onClose: () => void
}

/**
 * Display calibration history for a hand.
 */
const CalibrationHistory: React.FC<CalibrationHistoryProps> = ({ handId, onClose }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['calibrations', handId],
    queryFn: () => handsApi.getCalibrations(handId),
  })

  if (isLoading) {
    return <div className="text-center py-4">Loading calibrations...</div>
  }

  const calibrations = data?.data || []

  if (calibrations.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        No calibrations recorded for this hand.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {calibrations.map((cal: Calibration) => (
        <div key={cal.id} className="border rounded-lg p-4">
          <div className="flex justify-between items-start mb-2">
            <span className="font-medium">{cal.calibration_method}</span>
            <span className="text-sm text-gray-500">
              {new Date(cal.calibrated_at).toLocaleString()}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">ADC Offset:</span>
              <p className="font-mono">[{cal.adc_offset.join(', ')}]</p>
            </div>
            <div>
              <span className="text-gray-500">ADC Scale:</span>
              <p className="font-mono">[{cal.adc_scale.join(', ')}]</p>
            </div>
          </div>
          {cal.notes && (
            <p className="text-sm text-gray-600 mt-2">{cal.notes}</p>
          )}
        </div>
      ))}

      <div className="flex justify-end pt-4 border-t">
        <button onClick={onClose} className="btn-secondary">
          Close
        </button>
      </div>
    </div>
  )
}

// =============================================================================
// Main Page Component
// =============================================================================

const HandsPage: React.FC = () => {
  const queryClient = useQueryClient()

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedHand, setSelectedHand] = useState<Hand | null>(null)
  const [calibrationHandId, setCalibrationHandId] = useState<string | null>(null)
  const [tensometerCalibrationHand, setTensometerCalibrationHand] = useState<Hand | null>(null)

  // Filters
  const [statusFilter, setStatusFilter] = useState('')

  // Fetch hands
  const { data, isLoading } = useQuery({
    queryKey: ['hands', statusFilter],
    queryFn: () => handsApi.list({
      status: statusFilter || undefined,
    }),
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: HandCreate) => handsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hands'] })
      setIsModalOpen(false)
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: HandUpdate }) =>
      handsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hands'] })
      setIsModalOpen(false)
      setSelectedHand(null)
    },
  })

  // Helper function for date display
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Never'

    const date = new Date(dateString)
    return date.toLocaleDateString('sk-SK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  // Table columns with sorting enabled
  const columns: Column<Hand>[] = [
    {
      key: 'hardware_id',
      header: 'Hardware ID',
      accessor: 'hardware_id',
      sortable: true,
      searchable: true,
    },
    {
      key: 'hand_type',
      header: 'Type',
      sortable: true,
      sortFn: (a, b) => (a.hand_type || '').localeCompare(b.hand_type || ''),
      render: (hand) => (
        <span className={`badge ${hand.hand_type === 'left' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>
          {hand.hand_type === 'left' ? 'Left' : 'Right'}
        </span>
      ),
    },
    {
      key: 'hardware_version',
      header: 'Version',
      accessor: 'hardware_version',
      sortable: true,
    },
    {
      key: 'institution',
      header: 'Institution',
      sortable: true,
      sortFn: (a, b) => (a.institution_name || '').localeCompare(b.institution_name || ''),
      searchable: true,
      searchFn: (item, query) => (item.institution_name || '').toLowerCase().includes(query),
      render: (hand) => hand.institution_name || '-',
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      sortFn: (a, b) => (a.status || '').localeCompare(b.status || ''),
      render: (hand) => (
        <span className={`badge ${
          hand.status === 'active' ? 'badge-active' :
          hand.status === 'maintenance' ? 'badge-warning' :
          'badge-inactive'
        }`}>
          {hand.status}
        </span>
      ),
    },
    {
      key: 'last_calibrated',
      header: 'Last Calibrated',
      sortable: true,
      sortFn: (a, b) => {
        const aTime = a.last_calibrated_at ? new Date(a.last_calibrated_at).getTime() : 0
        const bTime = b.last_calibrated_at ? new Date(b.last_calibrated_at).getTime() : 0
        return aTime - bTime
      },
      render: (hand) => formatDate(hand.last_calibrated_at),
    },
    {
      key: 'tensometers',
      header: 'Tensometers',
      sortable: true,
      sortFn: (a, b) => {
        const aCount = a.tensometers?.filter(t => t.status === 'active').length || 0
        const bCount = b.tensometers?.filter(t => t.status === 'active').length || 0
        return aCount - bCount
      },
      render: (hand) => (
        <span className="text-sm">
          {hand.tensometers?.filter(t => t.status === 'active').length || 0}/5 active
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (hand) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation()
              setSelectedHand(hand)
              setIsModalOpen(true)
            }}
            className="text-primary-600 hover:text-primary-800 text-sm font-medium"
          >
            Edit
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              setTensometerCalibrationHand(hand)
            }}
            className="text-green-600 hover:text-green-800 text-sm font-medium"
          >
            Tensometers
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              setCalibrationHandId(hand.id)
            }}
            className="text-purple-600 hover:text-purple-800 text-sm font-medium"
          >
            History
          </button>
        </div>
      ),
    },
  ]

  // Handle form submit
  const handleSubmit = (formData: HandCreate | HandUpdate) => {
    if (selectedHand) {
      updateMutation.mutate({ id: selectedHand.id, data: formData as HandUpdate })
    } else {
      createMutation.mutate(formData as HandCreate)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Hands</h2>
          <p className="text-sm text-gray-600">
            Manage hand hardware and calibrations.
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedHand(null)
            setIsModalOpen(true)
          }}
          className="btn-primary"
        >
          Add Hand
        </button>
      </div>

      {/* Info Card */}
      <div className="card bg-blue-50 border-blue-200">
        <p className="text-sm text-blue-800">
          Each hand has 5 tensometers (thumb, index, middle, ring, little).
          Tensometers are automatically created when you add a new hand.
        </p>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-4">
          {/* Status Filter */}
          <div className="w-48">
            <select
              className="input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={data?.data || []}
        loading={isLoading}
        getRowKey={(hand) => hand.id}
        emptyMessage="No hands found"
        searchable
        searchPlaceholder="Search by hardware ID, institution..."
      />

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setSelectedHand(null)
        }}
        title={selectedHand ? 'Edit Hand' : 'Add Hand'}
        size="lg"
      >
        <HandForm
          hand={selectedHand}
          onSubmit={handleSubmit}
          onCancel={() => {
            setIsModalOpen(false)
            setSelectedHand(null)
          }}
          loading={createMutation.isPending || updateMutation.isPending}
        />
      </Modal>

      {/* Calibration History Modal */}
      <Modal
        isOpen={!!calibrationHandId}
        onClose={() => setCalibrationHandId(null)}
        title="Calibration History"
        size="lg"
      >
        {calibrationHandId && (
          <CalibrationHistory
            handId={calibrationHandId}
            onClose={() => setCalibrationHandId(null)}
          />
        )}
      </Modal>

      {/* Tensometer Calibration Modal */}
      <Modal
        isOpen={!!tensometerCalibrationHand}
        onClose={() => setTensometerCalibrationHand(null)}
        title={`Tensometer Calibration - ${tensometerCalibrationHand?.hardware_id || ''}`}
        size="lg"
      >
        {tensometerCalibrationHand && (
          <TensometerCalibration
            handId={tensometerCalibrationHand.id}
            onClose={() => setTensometerCalibrationHand(null)}
          />
        )}
      </Modal>
    </div>
  )
}

export default HandsPage
